import { IComponentOptions, IScope } from "angular";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable, take, takeUntil } from "rxjs";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { GtmhubController } from "@gtmhub/core";
import { IIndicator } from "@gtmhub/error-handling";
import { QuantivePlusSuggestions } from "@gtmhub/goals/models";
import { Ng1DestroyService } from "@gtmhub/shared/services";
import { INgRedux } from "@gtmhub/state-management";
import { TaskService } from "@gtmhub/tasks/services/task.service";
import { ITaskCreatedArgs, ITaskDeletedArgs, ITaskUpdatedArgs, TaskEvents } from "@gtmhub/tasks/task.events";
import { IPermissionsStoreState, hasPermissions } from "@gtmhub/users/redux";
import { AssigneeType } from "@webapp/assignees/models/assignee.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { Goal } from "@webapp/okrs/goals/models/goal.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { IQuantivePlusAcceptedTask, IQuantivePlusTask } from "@webapp/platform-intelligence/quantive-plus/models";
import { PiImprovementFacade } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/pi-improvement-facade.service";
import { generateImprovementPayload } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/utils";
import {
  PIStateProcessorInstanceSubType,
  PiStateProcessorInstance,
} from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/state-processor/pi-state-processor.models";
import { PiFeatureVisibilityFacade } from "@webapp/platform-intelligence/shared/services/pi-feature-visibility-facade.service";
import { PiSuggestionSharedGoalStateService } from "@webapp/platform-intelligence/shared/services/pi-suggestion-shared-goal-state.service";
import { PiSuggestionSharedMetricStateService } from "@webapp/platform-intelligence/shared/services/pi-suggestion-shared-metric-state.service";
import { getPiErrorData } from "@webapp/platform-intelligence/shared/utils/pi-errors.utils";
import { FlowName, KeyResultFlowName, ObjectiveFlowName } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { Task, TaskParentType } from "@webapp/tasks/models/tasks.models";

export interface OkrTasksBindings {
  parentId: string;
  item: Goal | Metric;
  parentType: TaskParentType;
  suggestions: QuantivePlusSuggestions;
  hasInlineSuggestions: boolean;
  handleKrTaskSuggestionsOnTaskAdded(args?: { suggestionIndex: number; currentMetricTasksCount: number; flowName: KeyResultFlowName }): void;
  handleKrTaskSuggestionsOnTaskRemoved(args: { currentMetricTasksCount: number; flowName: KeyResultFlowName }): void;
  handleGoalTaskSuggestionsOnTaskAdded(args?: { suggestionIndex: number; currentGoalTasksCount: number; flowName: ObjectiveFlowName }): void;
  handleGoalTaskSuggestionsOnTaskRemoved(args: { currentGoalTasksCount: number; flowName: ObjectiveFlowName }): void;
  onContextualSuggestionsToggle(opts: { state: boolean; subEntity: PIStateProcessorInstanceSubType }): void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface OkrTasksCtrl extends OkrTasksBindings {}
export class OkrTasksCtrl extends GtmhubController {
  public taskList: Task[];
  public hasManageTasksPermission: boolean;
  public isSuggestingTasksToOKRsEnabled: boolean;
  public indicators: {
    loading?: IIndicator;
  };
  public tasksInstance: PiStateProcessorInstance;
  public isTasksSuggestionVisible: boolean;
  public piContextualSuggestionsEnabled: boolean;

  public static $inject = [
    "$scope",
    "$ngRedux",
    "TaskService",
    "PiFeatureVisibilityFacade",
    "PiSuggestionSharedGoalStateService",
    "PiSuggestionSharedMetricStateService",
    "PiImprovementFacade",
    "FeatureTogglesFacade",
  ];
  private taskEvents: TaskEvents;
  private destroy$: Observable<unknown>;

  constructor(
    private $scope: IScope,
    private $ngRedux: INgRedux,
    private taskService: TaskService,
    private piFeatureVisibilityFacade: PiFeatureVisibilityFacade,
    private piSuggestionSharedGoalStateService: PiSuggestionSharedGoalStateService,
    private piSuggestionSharedMetricStateService: PiSuggestionSharedMetricStateService,
    private piImprovementFacade: PiImprovementFacade,
    private featureToggleFacade: FeatureTogglesFacade
  ) {
    super();

    this.hasManageTasksPermission = hasPermissions($ngRedux.getState<IPermissionsStoreState>(), "ManageTasks");
    this.indicators = {};
    this.taskList = [];

    this.taskEvents = new TaskEvents($scope);
    this.taskEvents.onTaskCreated((args) => this.onTaskCreated(args));
    this.taskEvents.onTaskDeleted((args) => this.onTaskDeleted(args));
    this.taskEvents.onTaskUpdated((args) => this.onTaskUpdated(args));

    this.destroy$ = new Ng1DestroyService(this.$scope);

    this.featureToggleFacade
      .isFeatureAvailable$(FeatureFlag.PiContextualSuggestions)
      .pipe(take(1))
      .subscribe((enabled) => {
        this.piContextualSuggestionsEnabled = enabled;
        this.isTasksSuggestionVisible = true;

        // we make them hidden if feature is enabled, otherwise this is working in contrast with the pi-feedback-card
        // TODO: iliya - remove this feature check when we remove the isPiFeedbackCardEnabled checks
        if (enabled) {
          this.isTasksSuggestionVisible = false;
        }

        this.$scope.$evalAsync();
      });
  }

  public $onInit(): void {
    if (this.parentType === "goal") {
      this.piSuggestionSharedGoalStateService
        .getSingleTask$()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (args) => this.onTaskSubjectUpdated(args),
        });
    }

    if (this.parentType === "metric") {
      this.piSuggestionSharedMetricStateService
        .getSingleTask$()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (args) => this.onTaskSubjectUpdated(args),
        });
    }

    this.loadTasks();
  }

  public isAtLeastOneTaskHidden(): boolean {
    return this.suggestions?.tasks?.some((task) => task.isHidden);
  }

  public setTasksSuggestionVisibility(): void {
    if (!this.isAtLeastOneTaskHidden()) {
      this.isTasksSuggestionVisible = !this.isTasksSuggestionVisible;
      this.onContextualSuggestionsToggle({ state: this.isTasksSuggestionVisible, subEntity: "tasks" });

      return;
    }

    this.suggestions.tasks.forEach((task) => (task.isHidden = false));
  }

  private onTaskSubjectUpdated(args: Partial<IQuantivePlusAcceptedTask>): void {
    if (args.type === "replacing") {
      const clonedSuggestions = angular.copy(this.suggestions.tasks);
      const suggestion = clonedSuggestions[args.quantivePlusSuggestionIndex];
      suggestion.title = args.newSuggestionName;

      this.suggestions.tasks = clonedSuggestions;
      this.$scope.$evalAsync();

      return;
    }

    if (args.type === "improving") {
      const suggestion = this.suggestions.tasks[args.quantivePlusSuggestionIndex];
      const clonedSuggestion = angular.copy(this.suggestions.tasks[args.quantivePlusSuggestionIndex]);
      delete clonedSuggestion.isLoading;

      const payload = generateImprovementPayload({
        subEntityType: "metricsAndTasks",
        instruction: args.improvement,
        suggestion: clonedSuggestion,
        isAskAI: true,
        improveSuggestionContext: args.improveSuggestionContext,
      });

      this.postTaskImprovement(payload, suggestion, args.quantivePlusSuggestionIndex);
      return;
    }

    if (args.type === "backToSuggestion") {
      const suggestion = this.suggestions.tasks[args.quantivePlusSuggestionIndex];

      const clonedSuggestion = (({ description, title, id }: IQuantivePlusTask): IQuantivePlusTask => ({
        description,
        title,
        id,
      }))(suggestion);

      this.suggestions.tasks[args.quantivePlusSuggestionIndex] = clonedSuggestion;

      return;
    }

    if (args.task) {
      this.onQuantivePlusTaskCreated(args as IQuantivePlusAcceptedTask);
    }
  }

  private postTaskImprovement(payload, suggestion: IQuantivePlusTask, suggestionIndex: number): void {
    suggestion.isLoading = true;

    this.$scope.$digest();

    this.piImprovementFacade
      .postImprovement<IQuantivePlusTask>(payload)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          suggestion.isLoading = false;

          const clonedSuggestions = angular.copy(this.suggestions.tasks);
          clonedSuggestions[suggestionIndex] = data.suggestions.improvement;

          this.suggestions.tasks = clonedSuggestions;

          this.$scope.$evalAsync();
        },
        error: (err: HttpErrorResponse) => {
          suggestion.isError = true;
          suggestion.isLoading = false;
          suggestion.errorData = getPiErrorData("task", err);

          this.$scope.$evalAsync();
        },
      });
  }

  public ownerAssigneeType(task: Task): AssigneeType {
    const { assignees } = this.$ngRedux.getState<IAssigneesStoreState>();
    return assignees.map[task.ownerId]?.type;
  }

  public onQuantivePlusTaskCreated(args: IQuantivePlusAcceptedTask): void {
    this.taskEvents.broadcastTaskCreated({
      task: args.task,
      sessionId: args.sessionId,
      quantivePlusSuggestionIndex: args.quantivePlusSuggestionIndex,
      isQuantivePlusOrigin: true,
      flowName: args.flowName,
    });
    this.$scope.$applyAsync();
  }

  public archiveTask(task: Task): void {
    const partialTask = { archived: true };
    this.taskService.update(task.id, partialTask).then((updatedTask) => {
      this.taskEvents.broadcastTaskUpdated({ task: updatedTask, update: partialTask, sessionId: this.item.sessionId });
    });
  }

  private loadTasks(): void {
    this.piFeatureVisibilityFacade
      .isSuggestingTasksToOKRsVisible$()
      .pipe(take(1))
      .subscribe((enabled) => {
        this.isSuggestingTasksToOKRsEnabled = enabled;
        this.$scope.$evalAsync();
      });

    this.indicators.loading = { progress: true };

    this.taskService
      .getTasksV2({ filter: { $and: [{ parentId: this.parentId }, { $or: [{ archived: { $exists: false } }, { archived: false }] }] }, sort: "-dueDate,dateCreated" })
      .then(
        (result) => {
          delete this.indicators.loading;
          this.taskList = result.items;

          if (this.parentType === "goal") {
            this.piSuggestionSharedGoalStateService.setTaskList(this.taskList);
          }
          if (this.parentType === "metric") {
            this.piSuggestionSharedMetricStateService.setTaskList(this.taskList);
          }
        },
        (error) => (this.indicators.loading = { error })
      );
  }

  private onTaskCreated(args: ITaskCreatedArgs): void {
    if (args.task.parentId === this.parentId) {
      if (args.isQuantivePlusOrigin) {
        this.taskList.push(args.task);
      } else {
        this.taskList.unshift(args.task);
      }

      if (this.isSuggestingTasksToOKRsEnabled) {
        this.handleSuggestionsOnTaskAdded({
          parentType: args.task.parentType,
          currentMetricTasksCount: this.taskList.length,
          currentGoalTasksCount: this.taskList.length,
          ...(args.isQuantivePlusOrigin && { suggestionIndex: args.quantivePlusSuggestionIndex }),
          flowName: args.flowName,
        });
      }
    }
  }

  private onTaskDeleted(args: ITaskDeletedArgs): void {
    this.taskList = this.taskList.filter((task) => task.id !== args.task.id);

    if (this.isSuggestingTasksToOKRsEnabled) {
      this.handleSuggestionsOnTaskRemoved({ parentType: args.task.parentType, currentTasksCount: this.taskList.length });
    }
  }

  private onTaskUpdated(args: ITaskUpdatedArgs): void {
    const taskIndex = this.taskList.findIndex((task) => task.id === args.task.id);
    const taskIsInList = taskIndex >= 0;

    // if task changed parent => add to task list
    if (!taskIsInList) {
      if (args.task.parentId === this.parentId) {
        this.taskList.unshift(args.task);

        if (this.isSuggestingTasksToOKRsEnabled) {
          this.handleSuggestionsOnTaskAdded({
            parentType: args.task.parentType,
            currentMetricTasksCount: this.taskList.length,
            currentGoalTasksCount: this.taskList.length,
          });
        }
      }

      return;
    }

    // if parent of the task is changed or is archived, remove it from the task list
    if (this.parentId !== args.task.parentId || args.task.archived) {
      this.taskList.splice(taskIndex, 1);

      if (this.isSuggestingTasksToOKRsEnabled) {
        this.handleSuggestionsOnTaskRemoved({ parentType: this.parentType, currentTasksCount: this.taskList.length });
      }

      return;
    }

    // and update task in list
    this.taskList[taskIndex] = args.task;
  }

  private handleSuggestionsOnTaskAdded = (args: {
    parentType: TaskParentType;
    suggestionIndex?: number;
    currentMetricTasksCount: number;
    currentGoalTasksCount: number;
    flowName?: FlowName;
  }): void => {
    if (args.parentType === "goal") {
      this.handleGoalTaskSuggestionsOnTaskAdded(
        (args.suggestionIndex || args.suggestionIndex === 0) && {
          suggestionIndex: args.suggestionIndex,
          currentGoalTasksCount: args.currentGoalTasksCount,
          flowName: args.flowName as ObjectiveFlowName,
        }
      );
    }

    if (args.parentType === "metric") {
      this.handleKrTaskSuggestionsOnTaskAdded(
        (args.suggestionIndex || args.suggestionIndex === 0) && {
          suggestionIndex: args.suggestionIndex,
          currentMetricTasksCount: args.currentMetricTasksCount,
          flowName: args.flowName as KeyResultFlowName,
        }
      );
    }
  };

  private handleSuggestionsOnTaskRemoved = (args: { parentType: TaskParentType; currentTasksCount: number; flowName?: FlowName }): void => {
    if (args.parentType === "goal") {
      this.handleGoalTaskSuggestionsOnTaskRemoved({ currentGoalTasksCount: args.currentTasksCount, flowName: args.flowName as ObjectiveFlowName });
    }

    if (args.parentType === "metric") {
      this.handleKrTaskSuggestionsOnTaskRemoved({ currentMetricTasksCount: args.currentTasksCount, flowName: args.flowName as KeyResultFlowName });
    }
  };
}

export const OkrTasksComponent: IComponentOptions = {
  template: require("./okr-tasks.component.html"),
  controller: OkrTasksCtrl,
  bindings: {
    parentId: "<",
    item: "<",
    parentType: "@",
    tasksInstance: "<",
    suggestions: "<",
    suggestionsStatus: "<",
    hasInlineSuggestions: "<",
    isRemoveBtnAvailable: "<",
    handleKrTaskSuggestionsOnTaskAdded: "&",
    handleKrTaskSuggestionsOnTaskRemoved: "&",
    handleGoalTaskSuggestionsOnTaskAdded: "&",
    handleGoalTaskSuggestionsOnTaskRemoved: "&",
    onContextualSuggestionsToggle: "&",
  },
};
